import {
  AlertTriangle,
  ArrowRight,
  Check,
  FileText,
  ClipboardList,
  ClipboardCheck,
  Calendar,
  Flag,
  Activity,
  Eye,
  MessageSquare,
  RefreshCw,
  RotateCcw,
  TrendingUp,
  LogIn,
  Undo2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export const ACTIVITY_EVENT_ICONS: Record<string, typeof Activity> = {
  behavior_alert: AlertTriangle,
  alert_flag: Flag,
  location_transfer: ArrowRight,
  re_intake: RotateCcw,
  initial_intake: LogIn,
  outcome: Check,
  outcome_cleared: Undo2,
  file_upload: FileText,
  task_linked: ClipboardList,
  meeting_linked: Calendar,
  observation_added_to_task: Eye,
  observation_added_to_meeting: Eye,
  animal_added_to_task: ClipboardList,
  animal_added_to_meeting: Calendar,
  status_change: RefreshCw,
  adoption_level_change: TrendingUp,
  eval_status_change: ClipboardCheck,
  observation_logged: MessageSquare,
};

export const ACTIVITY_EVENT_COLORS: Record<string, string> = {
  behavior_alert: "bg-amber-500",
  alert_flag: "bg-red-500",
  location_transfer: "bg-blue-500",
  re_intake: "bg-cyan-500",
  initial_intake: "bg-emerald-500",
  outcome: "bg-green-500",
  outcome_cleared: "bg-slate-400",
  file_upload: "bg-violet-500",
  task_linked: "bg-slate-500",
  meeting_linked: "bg-indigo-500",
  observation_added_to_task: "bg-teal-500",
  observation_added_to_meeting: "bg-teal-500",
  animal_added_to_task: "bg-slate-500",
  animal_added_to_meeting: "bg-indigo-500",
  status_change: "bg-sky-500",
  adoption_level_change: "bg-emerald-500",
  eval_status_change: "bg-orange-500",
  observation_logged: "bg-teal-500",
};

// Re-export canonical category constants from the shared package so
// consumers of this module only need one import path.
export {
  ACTIVITY_CATEGORIES,
  ACTIVITY_CATEGORY_OPTIONS,
  ACTIVITY_CATEGORY_EMPTY_LABEL,
  ACTIVITY_CATEGORY_EVENT_TYPES,
} from "@mspca-beti/shared";

export function getActivityEventLabel(eventType: string, source?: string): string {
  switch (eventType) {
    case "behavior_alert": return "Behavior Alert";
    case "alert_flag": return "Alert Flag";
    case "location_transfer": return "Transfer";
    case "re_intake": return "Readmission";
    case "initial_intake": return "Arrival";
    case "outcome": return "Outcome";
    case "outcome_cleared": return "Outcome Cleared";
    case "file_upload": return "File Upload";
    case "task_linked": return "Task";
    case "meeting_linked": return "Meeting";
    case "observation_added_to_task": return "Task";
    case "observation_added_to_meeting": return "Meeting";
    case "animal_added_to_task": return "Task";
    case "animal_added_to_meeting": return "Meeting";
    case "status_change": return "Status Change";
    case "adoption_level_change": return "Adoption Level";
    case "eval_status_change": return "Eval Status";
    case "observation_logged": return "Observation";
    default: return source === "event" ? "Event" : "Task";
  }
}

export function getActivityDisplayTitle(
  eventType: string,
  rawTitle: string,
  source: "event" | "task" | "meeting" | string,
  animalName?: string | null,
): { displayTitle: string; subtitle: string | null } {
  const name = animalName || "Animal";

  if (source === "task" || eventType === "task_linked" || eventType === "animal_added_to_task") {
    return {
      displayTitle: `${name} added to task`,
      subtitle: rawTitle,
    };
  }
  if (source === "meeting" || eventType === "meeting_linked" || eventType === "animal_added_to_meeting") {
    return {
      displayTitle: `${name} added to meeting agenda`,
      subtitle: rawTitle,
    };
  }

  return {
    displayTitle: rawTitle,
    subtitle: null,
  };
}

export function getScoreBandBadgeClass(scoreBand: string | null | undefined): string {
  switch (scoreBand) {
    case "GREEN": return "bg-success/10 text-success border-success/20";
    case "YELLOW": return "bg-warning/10 text-warning border-warning/20";
    case "RED": return "bg-destructive/10 text-destructive border-destructive/20";
    default: return "bg-muted text-muted-foreground";
  }
}

export function parseDateSafe(s: string | null | undefined): Date {
  if (!s) return new Date();
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const d = new Date(dateOnly ? s + "T00:00:00" : s);
  return isNaN(d.getTime()) ? new Date() : d;
}

export function formatActivityTimestamp(date: Date): string {
  const now = new Date();
  if (date > now) {
    return "just now";
  }
  return formatDistanceToNow(date, { addSuffix: true });
}

// R3 C3: shared date-group label helpers (also used by AnimalInteractionTimeline)
export function getDateGroupLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function getDateGroupLabelLong(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
